import { API_CALL, CALENDAR_UPDATE_PRICING_DATA, CLEAR_BOOKING_CALENDAR } from './actionConstants'

import { FETCH_CALENDARS, FETCH_CALENDARS_BY_OCCUPANCY, FETCH_TOUR_DATES, FETCH_AVAILABILITY_DATE_RANGE } from './apiActionConstants'
import type { CalendarParams } from 'api/calendarV2'
import { getCalendars, getCalendarsByOccupancy, getAvailabilityDateRange } from 'api/calendarV2'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { tourEnquiry } from 'api/reservation'
import getObjectKey from 'lib/object/getObjectKey'
import type { AppAction } from './types/ActionTypes'
import { getChannelMarkupSource } from 'selectors/channelMarkupSelector'

export function clearBookingCalendar() {
  return {
    type: CLEAR_BOOKING_CALENDAR,
  }
}

interface FetchCalendarOptions {
  occupants?: Array<App.Occupants>;
  flightOrigin?: string;
  minDate?: string;
  packages?: Array<App.HotelPackage>;
  packageKeys?: Array<string>;
  regionCode?: string;
  reservationId?: string;
  bypassRegionFilter?: boolean;
  lereGroup?: string;
}

export function fetchCalendars(offerId: string, options: FetchCalendarOptions = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const offer = (state.offer.offers[offerId] ?? state.offer.offerSummaries[offerId]) as App.HotelOffer

    const packages = options.packages ?? offer?.packages ?? []
    const pkgLereGroupMap = state.calendar.calendarsByPackageKeyLereGroups ?? {}
    const packagesToFetch = packages.filter(pkg =>
      // remove any keys we're already loading or have loaded
      (!state.calendar.calendarsByPackageKey[pkg.uniqueKey] || pkgLereGroupMap[pkg.uniqueKey] !== options.lereGroup) &&
      !state.calendar.calendarsLoading[pkg.uniqueKey],
    )
    const markupSource = getChannelMarkupSource(state)

    if (
      // offer is already loading (without knowledge of it's package keys)
      ((!options.packages && state.calendar.calendarsLoading[offerId]) ||
      // we know what package keys are loading and there are none
        (packages.length > 0 && packagesToFetch.length === 0))
    ) {
      return
    }

    dispatch({
      api: FETCH_CALENDARS,
      type: API_CALL,
      offerId: offer.id,
      packageKeys: packagesToFetch.map(pkg => pkg.uniqueKey),
      lereGroup: options.lereGroup,
      request: () => getCalendars(
        offer,
        options.regionCode || state.geo.currentRegionCode,
        {
          occupants: options.occupants ?? [],
          flightOrigin: options.flightOrigin,
          minDate: options.minDate,
          packages: packagesToFetch,
          enquiryType: state.auth.account.isSpoofed ? 'admin' : 'customer',
          reservationId: options?.reservationId,
          postPurchase: state.checkout.cart.mode,
          bypassRegionFilter: options.bypassRegionFilter,
          lereGroup: options.lereGroup,
        },
        markupSource,
      ),
    })
  }
}

type CalendarRequestItem = {
  occupancyKey: string
  params: CalendarParams
}

export function fetchCalendarsByOccupancy(offerId: string, options: FetchCalendarOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const offer = (state.offer.offers[offerId] ?? state.offer.offerSummaries[offerId]) as App.HotelOffer
    const markupSource = getChannelMarkupSource(state)

    const packages = options?.packages ?? []
    const deduplicateCalls = new Set<string>()
    const requests = (options?.occupants ?? []).reduce<Array<CalendarRequestItem>>((acc, occ) => {
      const occupancyKey = generateOccupancyStringByRoom(occ)

      const pkgLoadingMap = state.calendar.calendarsByOccupancyLoading[occupancyKey] ?? {}
      const pkgLoadedMap = state.calendar.calendarsByOccupancy[occupancyKey] ?? {}
      const pkgLereGroupMap = state.calendar.calendarsByOccupancyLereGroups?.[occupancyKey] ?? {}

      /**
        * Fetch packages if they are both:
        * - not loading
        * - not loaded for the requested LERE group
        */
      const packagesToFetch = packages.filter(pkg =>
        !pkgLoadingMap[pkg.uniqueKey] &&
        (!pkgLoadedMap[pkg.uniqueKey] || pkgLereGroupMap[pkg.uniqueKey] !== options.lereGroup),
      )
      if (packagesToFetch.length > 0) {
        const params: CalendarParams = {
          occupants: [occ],
          packages: packagesToFetch,
          flightOrigin: options.flightOrigin,
          minDate: options.minDate,
          enquiryType: state.auth.account.isSpoofed ? 'admin' : 'customer',
          reservationId: options.reservationId,
          postPurchase: state.checkout.cart.mode,
          bypassRegionFilter: options.bypassRegionFilter,
          lereGroup: options.lereGroup,
        }
        const key = `${offerId}:${occupancyKey}:${packagesToFetch.map(pkg => pkg.uniqueKey).join(':')}`
        if (!deduplicateCalls.has(key)) {
          deduplicateCalls.add(key)
          acc.push({ occupancyKey, params })
        }
      }
      return acc
    }, [])

    if (requests.length === 0) {
      return
    }

    requests.forEach((request) => {
      const action: AppAction = {
        api: FETCH_CALENDARS_BY_OCCUPANCY,
        type: API_CALL,
        occupancyKey: request.occupancyKey,
        pkgKeys: request.params.packages.map(pkg => pkg.uniqueKey),
        lereGroup: request.params.lereGroup,
        request: () => getCalendarsByOccupancy(
          offer,
          options.regionCode || state.geo.currentRegionCode,
          request.params,
          markupSource,
        ),
      }
      dispatch(action)
    })
  }
}

export interface PricingData {
  hotelPrice: number
  hotelValue: number
  taxesAndFees: number
  propertyFees: number
  surcharge: number
}
export function updateCalendarPricingData(
  offerId: string,
  uniqueKey: string,
  checkIn: string,
  occupancy: string,
  data: Partial<PricingData>,
) {
  return {
    type: CALENDAR_UPDATE_PRICING_DATA,
    offerId,
    uniqueKey,
    checkIn,
    occupancy,
    data,
  }
}

export function getTourDates(
  tourId: string,
  tourOptionId: string,
  duration: number,
  adults: number,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = getObjectKey({ tourId, tourOptionId, duration, adults })

    if (
      state.calendar.calendarsLoading[key] ||
      state.calendar.calendarErrors[key] ||
      state.calendar.tourDates[key]
    ) {
      // already done, no need to try again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_DATES,
      request: () => tourEnquiry(tourId, tourOptionId, duration, adults),
      key,
    })
  }
}

export interface FetchAvailabilityDateRangeOptions {
  offerId: string
  date: string
  nights: number
  rooms: Array<App.Occupants>
  lereGroup?: string
}

const availabilityAbortControllers = new Map<string, AbortController>()

export function fetchAvailabilityDateRange(options: FetchAvailabilityDateRangeOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    availabilityAbortControllers.get(options.offerId)?.abort()
    const controller = new AbortController()
    availabilityAbortControllers.set(options.offerId, controller)

    dispatch({
      type: API_CALL,
      api: FETCH_AVAILABILITY_DATE_RANGE,
      offerId: options.offerId,
      request: () => getAvailabilityDateRange({
        offerId: options.offerId,
        date: options.date,
        nights: options.nights,
        rooms: options.rooms,
        region: state.geo.currentRegionCode,
        currencyCode: state.geo.currentCurrency,
        lereGroup: options.lereGroup,
      }, controller.signal),
    })
  }
}
